/**
 * Analytics Tracking Service
 *
 * Tracks user events for conversion funnel analytics.
 * These events are sent to the public /api/analytics/track endpoint.
 */

// Get API URL - same as in api.ts
const defaultApiUrl = process.env.NODE_ENV === 'production'
  ? 'https://api.saillab.co.jp'
  : 'http://localhost:5000';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || defaultApiUrl}/api`;

export type AnalyticsEventType =
  // Sign-up funnel
  | 'signup_click'           // User clicked email sign-up button
  | 'signup_oauth_google'    // User clicked Google OAuth sign-up
  | 'signup_oauth_microsoft' // User clicked Microsoft OAuth sign-up
  | 'signup_email'           // User submitted the email/password sign-up form
  | 'account_created'        // Account was created
  | 'first_meeting_created'  // User created their first meeting type
  | 'first_booking_received' // User received their first booking
  | 'subscription_started'   // User started a paid subscription
  // Booking funnel (host Insights page) — all require meetingTypeId
  | 'booking_page_viewed'    // Invitee opened a public booking page
  | 'booking_slot_selected'  // Invitee picked a time slot
  | 'booking_confirmed';     // Invitee reached the confirmation screen

interface TrackEventParams {
  eventType: AnalyticsEventType;
  sessionId?: string;
  meetingTypeId?: number;
  metadata?: Record<string, unknown>;
}

// Generate or retrieve session ID for tracking
const getSessionId = (): string => {
  const SESSION_KEY = 'saillab_session_id';
  let sessionId = sessionStorage.getItem(SESSION_KEY);

  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }

  return sessionId;
};

/**
 * Booking pages are served by the same app as the dashboard, so a visitor who
 * is signed in to SailLab still has their token in localStorage while looking at
 * one. Sending it lets the server recognise a host (or their colleague) opening
 * their OWN booking page and leave that visit out of the funnel on Insights.
 *
 * The endpoint stays public — no token means an ordinary anonymous visitor, and
 * they are counted as one.
 */
const optionalAuthHeader = (): Record<string, string> => {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    // Storage blocked (private mode / embedded context). Track anonymously.
    return {};
  }
};

/**
 * Track an analytics event
 *
 * @param params - Event parameters
 * @returns Promise that resolves when the event is tracked (or silently fails)
 */
export const trackEvent = async (params: TrackEventParams): Promise<void> => {
  try {
    const { eventType, meetingTypeId, metadata } = params;
    const sessionId = params.sessionId || getSessionId();

    // Use the API base URL with the analytics endpoint
    await fetch(`${API_BASE_URL}/analytics/track`, {
      method: 'POST',
      // Survive an immediate navigation — a confirmed booking on a meeting type
      // with a custom redirect URL leaves the page the moment it renders, and
      // without this the browser cancels the in-flight request.
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...optionalAuthHeader(),
      },
      body: JSON.stringify({
        eventType,
        sessionId,
        meetingTypeId,
        metadata,
      }),
    });
  } catch (error) {
    // Silently fail - analytics should not break the user experience
  }
};

/**
 * Track sign-up button click (email sign-up)
 */
export const trackSignupClick = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'signup_click', metadata });
};

/**
 * Track Google OAuth sign-up button click
 */
export const trackGoogleSignupClick = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'signup_oauth_google', metadata });
};

/**
 * Track Microsoft OAuth sign-up button click
 */
export const trackMicrosoftSignupClick = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'signup_oauth_microsoft', metadata });
};

/**
 * Track email/password sign-up form submission
 */
export const trackEmailSignupClick = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'signup_email', metadata });
};

/**
 * Track account creation
 */
export const trackAccountCreated = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'account_created', metadata });
};

/**
 * Track first meeting type creation
 */
export const trackFirstMeetingCreated = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'first_meeting_created', metadata });
};

/**
 * Track first booking received
 */
export const trackFirstBookingReceived = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'first_booking_received', metadata });
};

/**
 * Track subscription started
 */
export const trackSubscriptionStarted = async (metadata?: Record<string, unknown>): Promise<void> => {
  await trackEvent({ eventType: 'subscription_started', metadata });
};

/**
 * Booking funnel tracking (host Insights page)
 * ---------------------------------------------
 * Each step is counted as a DISTINCT SESSION server-side, so a repeat within
 * the same session is noise. We drop it here rather than pay for the request:
 * React re-renders, StrictMode double-mounts, a page reload, and an invitee
 * changing their mind about a slot would each otherwise write a row.
 *
 * The guard key is per (event, meeting type) so a visitor browsing two of a
 * host's booking pages is correctly counted once for each.
 */
const alreadyTrackedThisSession = (
  eventType: AnalyticsEventType,
  meetingTypeId: number
): boolean => {
  const key = `saillab_funnel_${eventType}_${meetingTypeId}`;
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
    return false;
  } catch {
    // sessionStorage unavailable (private mode / blocked). Track anyway —
    // the server's COUNT(DISTINCT session_id) still de-duplicates.
    return false;
  }
};

const trackBookingFunnelStep = async (
  eventType: AnalyticsEventType,
  meetingTypeId?: number | null
): Promise<void> => {
  // Preview meeting types use id -1; never let them reach real analytics.
  if (!meetingTypeId || meetingTypeId <= 0) return;
  if (alreadyTrackedThisSession(eventType, meetingTypeId)) return;

  await trackEvent({ eventType, meetingTypeId });
};

/**
 * Track a public booking page being opened (funnel step 1)
 */
export const trackBookingPageViewed = async (meetingTypeId?: number | null): Promise<void> => {
  await trackBookingFunnelStep('booking_page_viewed', meetingTypeId);
};

/**
 * Track a time slot being selected (funnel step 2)
 */
export const trackBookingSlotSelected = async (meetingTypeId?: number | null): Promise<void> => {
  await trackBookingFunnelStep('booking_slot_selected', meetingTypeId);
};

/**
 * Track a booking reaching the confirmation screen (funnel step 3)
 */
export const trackBookingConfirmed = async (meetingTypeId?: number | null): Promise<void> => {
  await trackBookingFunnelStep('booking_confirmed', meetingTypeId);
};

export default {
  trackEvent,
  trackSignupClick,
  trackGoogleSignupClick,
  trackMicrosoftSignupClick,
  trackEmailSignupClick,
  trackAccountCreated,
  trackFirstMeetingCreated,
  trackFirstBookingReceived,
  trackSubscriptionStarted,
  trackBookingPageViewed,
  trackBookingSlotSelected,
  trackBookingConfirmed,
  getSessionId,
};
